/**
 * Advanced-mode primitive — a reusable "simple vs. power-user" toggle that any
 * feature can adopt in two lines. The preference is persisted per feature
 * (localStorage, via zustand/persist) so a user's choice survives reloads and
 * stays in sync across every toggle bound to the same feature key.
 *
 * Usage:
 *
 *   const { advanced } = useAdvancedMode('content');
 *   …
 *   <AdvancedToggle feature="content" />
 *   <AdvancedSection feature="content" title="Options avancées">
 *     …power-user controls…
 *   </AdvancedSection>
 *
 * Everything is token-themed (works in light + dark) and accessible
 * (`role="switch"` + `aria-checked`). Toggling is cheap and local; nothing is
 * sent to the network.
 */

import { type ReactNode, useCallback } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useT, tr } from '@/i18n';

// ---------------------------------------------------------------------------
// Store — a map of feature key → advanced-on, plus the set of feature keys the
// user has explicitly touched (so a changed default doesn't override a choice).
// ---------------------------------------------------------------------------

interface AdvancedState {
  /** feature key → advanced enabled. */
  byFeature: Record<string, boolean>;
  /** feature keys the user has explicitly toggled at least once. */
  touched: Record<string, boolean>;
  set: (feature: string, on: boolean) => void;
  toggle: (feature: string) => void;
}

const useAdvancedStore = create<AdvancedState>()(
  persist(
    (set) => ({
      byFeature: {},
      touched: {},
      set: (feature, on) =>
        set((s) => ({
          byFeature: { ...s.byFeature, [feature]: on },
          touched: { ...s.touched, [feature]: true },
        })),
      toggle: (feature) =>
        set((s) => ({
          byFeature: { ...s.byFeature, [feature]: !s.byFeature[feature] },
          touched: { ...s.touched, [feature]: true },
        })),
    }),
    {
      name: 'psn-advanced',
      version: 1,
      partialize: (s) => ({ byFeature: s.byFeature, touched: s.touched }),
    },
  ),
);

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseAdvancedMode {
  /** Whether advanced mode is currently enabled for this feature. */
  advanced: boolean;
  /** Set advanced mode explicitly. */
  setAdvanced: (on: boolean) => void;
  /** Flip advanced mode. */
  toggle: () => void;
}

/**
 * Read + control the advanced-mode preference for one feature.
 *
 * @param feature   stable key (e.g. `content`, `publishing`, `copilot`).
 * @param defaultOn value used until the user first toggles this feature. Lets a
 *                  feature that must show its power controls by default (so it
 *                  stays a strict superset of prior behaviour) start enabled.
 */
export function useAdvancedMode(feature: string, defaultOn = false): UseAdvancedMode {
  const stored = useAdvancedStore((s) => s.byFeature[feature]);
  const touched = useAdvancedStore((s) => s.touched[feature]);
  const setInStore = useAdvancedStore((s) => s.set);
  const toggleInStore = useAdvancedStore((s) => s.toggle);

  const advanced = touched ? !!stored : defaultOn;

  const setAdvanced = useCallback((on: boolean) => setInStore(feature, on), [feature, setInStore]);
  const toggle = useCallback(() => toggleInStore(feature), [feature, toggleInStore]);

  return { advanced, setAdvanced, toggle };
}

// ---------------------------------------------------------------------------
// Toggle switch
// ---------------------------------------------------------------------------

export interface AdvancedToggleProps {
  feature: string;
  /** Label shown next to the switch. Defaults to "Mode avancé". */
  label?: string;
  /** Default state until the user first toggles (see useAdvancedMode). */
  defaultOn?: boolean;
  className?: string;
  /** Compact variant (no text label; icon-sized). */
  compact?: boolean;
}

/**
 * A small, accessible switch that flips advanced mode for a feature. Safe to
 * drop into any header — it never renames existing selectors, it only adds a
 * new `role="switch"` control labelled "Mode avancé".
 */
export function AdvancedToggle({
  feature,
  label = tr('shell.components.advancedMode.advancedMode'),
  defaultOn = false,
  className = '',
  compact = false,
}: AdvancedToggleProps) {
  const { t } = useT();
  const { advanced, toggle } = useAdvancedMode(feature, defaultOn);
  return (
    <button
      type="button"
      role="switch"
      aria-checked={advanced}
      aria-label={label}
      title={advanced ? t('shell.components.advancedMode.labelEnabled', { label }) : t('shell.components.advancedMode.labelDisabled', { label })}
      onClick={toggle}
      data-advanced={advanced ? 'on' : 'off'}
      className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-medium transition ${
        advanced
          ? 'border-primary/40 bg-primary/10 text-primary'
          : 'border-border bg-surface text-muted hover:bg-surface-2 hover:text-foreground'
      } ${className}`}
    >
      <span
        aria-hidden="true"
        className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition ${
          advanced ? 'bg-primary' : 'bg-border'
        }`}
      >
        <span
          className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition ${
            advanced ? 'translate-x-3.5' : 'translate-x-0.5'
          }`}
        />
      </span>
      {!compact && <span>{label}</span>}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Section wrapper
// ---------------------------------------------------------------------------

export interface AdvancedSectionProps {
  feature: string;
  children: ReactNode;
  /** Optional heading rendered above the advanced controls. */
  title?: string;
  /** Optional helper text under the heading. */
  hint?: string;
  defaultOn?: boolean;
  className?: string;
  /**
   * Render children without the dashed panel chrome (just show/hide by mode).
   * Use when gating controls that already live inside their own container.
   */
  bare?: boolean;
}

/**
 * Renders its children only when advanced mode is enabled for `feature`. In
 * simple mode it collapses to nothing, so the essentials stay uncluttered.
 */
export function AdvancedSection({
  feature,
  children,
  title,
  hint,
  defaultOn = false,
  className = '',
  bare = false,
}: AdvancedSectionProps) {
  const { advanced } = useAdvancedMode(feature, defaultOn);
  if (!advanced) return null;
  if (bare) return <>{children}</>;
  return (
    <div
      data-advanced-section={feature}
      className={`space-y-3 rounded-token border border-dashed border-border/70 bg-surface-2/40 p-3 ${className}`}
    >
      {(title || hint) && (
        <div className="flex flex-col gap-0.5">
          {title && (
            <span className="flex items-center gap-1.5 text-xs font-semibold text-muted">
              <span aria-hidden="true">⚙️</span>
              {title}
            </span>
          )}
          {hint && <span className="text-[11px] text-muted">{hint}</span>}
        </div>
      )}
      {children}
    </div>
  );
}
